/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */


import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import * as moment from 'moment/moment';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, EMPTY, Observable} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  pluck,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import {ESSService} from '../../+ess/employee-self.service';
import {DATE_MAX, DATE_MIN, ProfileMode} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {Link} from '../api';
import {ContractStatus, ProfileType} from '../employees/contract/contract.interfaces';
import {Contract, EmpFile} from '../employees/employees.model';
import {EmployeesService} from '../employees/employees.service';
import {FileChangesSessionService} from '../files/file-changes-session.service';
import {FileChangesService} from '../files/file-changes.service';
import {DataTransformer} from '../form-generator/data-transformer/data-transformer';
import {FormCardFieldComponent} from '../form-generator/form-card/form-card-field/form-card-field.component';
import {
  FormDescriptor,
  GroupZoneDescriptor,
  TabSheetDescriptor,
  Value,
  ZoneDescriptor,
  ZoneValues
} from '../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {LocalizationService} from '../localization/localization.service';
import {ModalService} from '../modal';
import {DateLocalizationPipe} from '../pipes/date-localization.pipe';
import {isMobileDevice} from '../utils/browser';
import {isPresent} from '../utils/lang';
import {ProcessInstance} from '../workflow/workflow-v2.interface';
import {getWorkflowKey, WorkflowV2Service} from '../workflow/workflow-v2.service';

import {Card, CardField, CardFieldsWithError} from './contract-data.interfaces';
import {ContractDataService} from './contract-data.service';
import {isBankingDetails} from '../pipes/translate-and-format-variables.pipe';

const getZonesFromMap = (descriptorsMap, value: string) => {
  const groupZoneDescriptorFromMap = descriptorsMap.has(value) ? descriptorsMap.get(value) : descriptorsMap.get('default');
  return groupZoneDescriptorFromMap.zones;
};
const isContractInactive = (contract: Contract) => contract.status === ContractStatus.INACTIVE;
const BANKING_DETAILS = 'BANKING_DETAILS';

@Component({
  selector: 'gp-contract-data',
  providers: [FileChangesSessionService],
  templateUrl: './contract-data.component.html',
  styleUrls: ['./contract-data.component.scss']
})
export class ContractDataComponent extends Destroyable implements OnInit, OnChanges, AfterContentChecked, OnDestroy {

  public isContractInactive = isContractInactive;

  @Input()
  public photoLink: Link;

  @Input()
  public employeeFirstName: string;

  @Input()
  public employeeLastName: string;

  @Input()
  public selectedContract: Contract;

  @Input()
  public contracts: Array<Contract>;

  @Input()
  public cliEmpId: number;

  @Input()
  public workflowName: string;

  @Input()
  public profileType: ProfileType;

  @Input()
  public profileMode: ProfileMode;

  @Output() backLinkClickEvent = new EventEmitter<null>();

  public isInactiveContract: boolean = false;
  public contractId: number;
  public cliRegId: number;

  public descriptor: FormDescriptor;
  public contractData: any;
  public saving: boolean;
  public editMode: boolean = false;
  public isMultipleCardType: boolean;
  public form: UntypedFormGroup;
  public cards: Card[];
  public zoneValues: ZoneValues[];
  public activeGroupZone: GroupZoneDescriptor;
  public loading: boolean;
  public effectiveDateControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  public minDate: Date = moment(DATE_MIN).startOf('day').toDate();
  public maxDate: Date = moment(DATE_MAX).startOf('day').toDate();

  public activeTabSheet: TabSheetDescriptor;
  public errorMessage: string;
  public groupNamesInValidation = [];

  private groupNamesInValidationSubject = new BehaviorSubject(null);

  @ViewChild('fieldComponent', {read: ViewContainerRef, static: true})
  private fieldComponent: ViewContainerRef;

  public isEss: boolean;
  public isMobile = false;
  public isCollapsed: boolean = true;
  public isFormDirty = false;

  private fields: ComponentRef<FormCardFieldComponent>[] = [];

  private employeeContracts: Contract[];

  public profileModes = ProfileMode;

  constructor(private formBuilder: UntypedFormBuilder,
              private fileChanges: FileChangesSessionService,
              private translateService: TranslateService,
              private formGeneratorService: FormGeneratorService,
              private componentFactoryResolver: ComponentFactoryResolver,
              private modalService: ModalService,
              private toastrService: ToastrService,
              private contractDataService: ContractDataService,
              public essService: ESSService,
              private localization: LocalizationService,
              private cdRef: ChangeDetectorRef,
              private renderer: Renderer2,
              private employeesService: EmployeesService,
              private fileChangesService: FileChangesService,
              private workflowV2Service: WorkflowV2Service,
              private translate: TranslateService,
              private dateLocalizationPipe: DateLocalizationPipe) {
    super();
    this.customPlaceholderGenerator = this.customPlaceholderGenerator.bind(this);
  }

  public ngOnInit() {
    this.isMobile = isMobileDevice();
    if (this.isMobile) {
      this.isCollapsed = false;
    }

    this.isEss = this.profileType === ProfileType.ESS;

    this.groupNamesInValidationSubject.pipe(
        switchMap(() => this.workflowV2Service.getOngoingRequests({
          key: getWorkflowKey(this.workflowName, this.cliRegId),
          contractId : this.selectedContract.id,
          'profile-mode': this.profileMode
        }).pipe(
          pluck('data'),
          tap((requests) => {
            const groupNameVariableValues = requests
              .flatMap(request => request.variables)
              .filter(variable => variable.name === 'groupName')
              .map(variable => isBankingDetails(variable.name, variable.value) ? variable.value.split('_')[0] : variable.value);
            this.groupNamesInValidation = uniq(groupNameVariableValues);

          })
        )),
        takeUntil(this.componentDestroyed$)).subscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedContract) {
      this.discardFileChanges();
      this.setContractData();
    }
  }

  public ngAfterContentChecked(): void {
    this.cdRef.detectChanges();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  @HostListener('window:resize', ['$event'])
  public onWindowResize() {
    this.isMobile = isMobileDevice();
  }

  public selectGroupZone(tabSheet?: TabSheetDescriptor, groupZone?: GroupZoneDescriptor): void {
    if (this.isMobile) {
      this.isCollapsed = !this.isCollapsed;
    }
    this.toggleTabSheet(tabSheet, groupZone);
  }

  private toggleTabSheet(tabSheet: TabSheetDescriptor, groupZone: GroupZoneDescriptor) {
    if (tabSheet) {
      this.descriptor.tabSheets.forEach((ts: TabSheetDescriptor) => ts.active = ts.tabSheetID === tabSheet.tabSheetID);

      this.activeTabSheet = tabSheet;

      if (groupZone && !this.isGroupZoneInActiveTab(tabSheet, groupZone)) {
        groupZone = null;
      }
      let selectedGroupZone = groupZone;
      if (!groupZone && !this.isMobile) {
        selectedGroupZone = tabSheet.groupZones[0];
      }
      this.onSelectGroupZone(selectedGroupZone);
    }
  }

  public isGroupZoneInActiveTab(tabSheet: TabSheetDescriptor, groupZone: GroupZoneDescriptor): boolean {
    return tabSheet.groupZones.some((zone) => zone.groupZoneID === groupZone.groupZoneID);
  }

  public onBackLinkClick() {
    this.backLinkClickEvent.emit(null);
  }

  public onSelectGroupZone(groupZone): void {
    this.activeGroupZone = groupZone;

    if (!this.activeGroupZone) {
      return;
    }
    const hasContractZone = this.activeGroupZone.zones.find(zone => zone.type === 'CONTRACT');
    if (hasContractZone) {
      this.computeEmployeeContracts();
    }

    this.isMultipleCardType = this.activeGroupZone.groupZoneType.includes('MULTIPLE');

    this.zoneValues = this.contractData
      .get(groupZone.groupZoneID)
      .sort((item1, item2) => {
        return moment(item2.begindate).startOf('day').diff(moment(item1.begindate).startOf('day'), 'days');
      });

    this.cards = this.zoneValues.map((zoneValue: ZoneValues) => this.getCardConfig(zoneValue));
  }

  public addOrEditCard(selectedCard?: Card): void {
    let zoneValues: ZoneValues;
    if (selectedCard) {
      zoneValues = this.zoneValues[this.cards.indexOf(selectedCard)];
    } else {
      zoneValues = this.formGeneratorService.createEmptyData(this.activeGroupZone)[0];
    }

    this.discardFileChanges();
    this.loadFields(zoneValues);

    this.modalService.openModalWithId('edit-card');
  }

  private discardFileChanges(): void {
    this.fileChangesService.discardPendingFileChanges(this.fileChanges)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public sendCardData(editPopup): void {
    if (!this.form.valid) {
      return;
    }

    let data = this.form.value;
    let groupZoneName = this.activeGroupZone.groupZoneID;
    const values = data[this.activeGroupZone.groupZoneID][0];

    if (values.id === null) {
      delete values.id;
    }

    if (!this.activeGroupZone.isDated) {
      values.begindate = DATE_MIN;
    }

    this.fields.forEach(f => {
      if (!isPresent(f.instance.value.value)) {
        f.instance.value.value = '';
      }
      values.values[f.instance.descriptor.zoneID] = f.instance.value;
      if (f.instance.descriptor.inputMask) {
        values.values[f.instance.descriptor.zoneID].value = DataTransformer.extractMask(
          values.values[f.instance.descriptor.zoneID].value,
          f.instance.descriptor.inputMask
        );
      }
    });

    this.formGeneratorService.transformZoneValues(values);

    if (this.activeGroupZone.internalType === BANKING_DETAILS) {
      const pmValue: Value = values.values[this.activeGroupZone.zones
        .find(z => z.isKeyZone || z.zoneID.includes('.paymentModality')).zoneID].value;

      const newData = {};
      groupZoneName = `${values.groupName}_${pmValue}`;
      Object.keys(values.values).forEach((zone: string) => newData[`${zone}_${pmValue}`] = values.values[zone]);
      data = {
        [groupZoneName]: [{
          begindate: values.begindate,
          isDated: false,
          groupName: groupZoneName,
          enddate: DATE_MAX,
          default: false,
          values: newData
        }]
      };
    }

    this.saving = true;
    const requestVariables = {
      ...data[groupZoneName][0],
      contractId: this.contractId,
      groupZoneTransId: this.activeGroupZone.transId,
      cliEmpId: this.cliEmpId
    };
    const updatePersonalInfo$ = this.contractDataService.createUpdatePersonalInfoWorkflowV2(
      requestVariables,
      this.workflowName,
      this.cliRegId,
      this.profileMode);

    this.fileChangesService.saveWithPendingFileChanges(this.fileChanges, updatePersonalInfo$).pipe(
      tap(() => {
        this.setContractData();
        this.toastrService.success(this.translateService.instant('EMPLOYEE_MANAGE.changes_sent'));
        this.groupNamesInValidationSubject.next(null);
        editPopup.close();
      }),
      catchError(() => EMPTY),
      finalize(() => this.saving = false),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public cancelCardEdit(editPopup): void {
    this.fileChangesService.discardPendingFileChanges(this.fileChanges).pipe(
      tap(() => editPopup.close()),
      catchError(() => EMPTY),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private setContractData(): void {
    this.loading = true;
    this.cliRegId = this.selectedContract ? this.selectedContract.cliRegID : null;
    this.contractId = this.selectedContract ? this.selectedContract.id : null;
    this.isInactiveContract = this.selectedContract ? this.selectedContract.status === ContractStatus.INACTIVE : false;
    const activeTabSheetId = this.getActiveTabSheetId();
    const activeGroupZoneName = this.getActiveGroupZoneName();

    this.contractDataService.getContractDataAndDescriptor(this.contractId, this.cliRegId, this.profileType)
      .pipe(
        tap(data => {
          this.descriptor = data.descriptor;
          this.contractData = data.contractData;
          this.toggleTheLastTabSheet(activeTabSheetId, activeGroupZoneName);
          this.groupNamesInValidationSubject.next(null);
        }),
        catchError(() => {
          this.descriptor = null;
          this.contractData = null;
          return EMPTY;
        }),
        switchMap(() => this.employeesService.getEmpFiles(this.contractId, '', true, this.profileMode)),
        takeUntil(this.componentDestroyed$),
        finalize(() => this.loading = false)
      )
      .subscribe();
  }

  private getActiveGroupZoneName(): string {
    return this.activeGroupZone ? this.activeGroupZone.groupZoneID : null;
  }

  private getActiveTabSheetId(): string {
    if (this.descriptor && this.descriptor.tabSheets.length) {
      const activeTabsheet = this.descriptor.tabSheets.find(tabSheet => tabSheet.active);
      return activeTabsheet?.tabSheetID;
    } else {
      return null;
    }
  }

  private toggleTheLastTabSheet(activeTabSheetId, activeGroupZoneName: string) {
    const previousActiveTabSheet = activeTabSheetId ? this.descriptor.tabSheets
      .find(tabSheet => tabSheet.tabSheetID === activeTabSheetId) : null;
    let selectedTabSheet = previousActiveTabSheet;
    if (!previousActiveTabSheet && !this.isMobile) {
      selectedTabSheet = this.descriptor.tabSheets[0];
    }
    this.activeTabSheet = selectedTabSheet;
    const previousActiveGroupZone = activeGroupZoneName && previousActiveTabSheet ? previousActiveTabSheet.groupZones
      .find(groupZone => groupZone.groupZoneID === activeGroupZoneName) : null;
    this.toggleTabSheet(this.activeTabSheet, previousActiveGroupZone);
  }

  private getCardConfig(zoneValue: ZoneValues): Card {
    const {fields, errorMessage} = this.getCardFieldsAndError(zoneValue);

    return {
      fields,
      errorMessage,
      validRangeLabel: this.getValidRangeLabel(zoneValue.begindate, zoneValue.enddate)
    };
  }

  private populateZonesAndGetErrorMessage(zoneValue: ZoneValues): string {
    if (this.activeGroupZone.internalType === BANKING_DETAILS) {
      const value: Value = zoneValue.values[this.activeGroupZone.zones
        .find(zoneDescriptor => zoneDescriptor.isKeyZone || zoneDescriptor.zoneID.includes('.paymentModality')).zoneID];

      const groupZoneDescriptorFromMap: Map<string, GroupZoneDescriptor> = this.activeGroupZone.metadatas.descriptors;
      if (value && value.value) {
        this.activeGroupZone.zones = getZonesFromMap(groupZoneDescriptorFromMap, value.value);
        return groupZoneDescriptorFromMap.has(value.value) ?
          null :
          this.translateService.instant('ERROR.employee_access_not_coherent_with_data',
            {groupZone: this.activeGroupZone.transId});
      } else {
        this.activeGroupZone.zones = groupZoneDescriptorFromMap.get('default').zones;
        return null;
      }
    }
    return null;
  }

  private computeEmployeeContracts(): void {
    if (this.isEss && !this.employeeContracts) {
      this.employeeContracts = this.essService.employee.contracts;
    }
    if (!this.isEss) {
      this.contractDataService.getContractsByEmployeeId(this.cliEmpId)
        .pipe(tap((value: Contract[]) => this.employeeContracts = value)).subscribe();
    }
  }

  private getCardFieldsAndError(zoneValues: ZoneValues): CardFieldsWithError {
    const errorMessage = this.populateZonesAndGetErrorMessage(zoneValues);
    const fields: CardField[] = this.getCardFields(zoneValues);

    return {fields, errorMessage};
  }

  private getCardFields(zoneValues: ZoneValues): CardField[] {
    return this.activeGroupZone.zones
      .sort((zoneA: ZoneDescriptor, zoneB: ZoneDescriptor) => zoneA.displayorder - zoneB.displayorder)
      .map((descriptor: ZoneDescriptor) => {
        const values = Object.assign({},
          zoneValues.values[descriptor.zoneID]
            ? zoneValues.values[descriptor.zoneID]
            : {value: '', type: descriptor.type});

        const control: UntypedFormControl = this.formBuilder.control({
          value: values.value
          // disabled: this.readOnly
        }, []);

        if (descriptor.type === 'FILE') {
          this.employeesService.empFiles$
            .subscribe((files: EmpFile[]) => {
              values.file = files.find(f => f.id.toString() === values.value.toString());
            });
        }

        return {
          descriptor,
          fieldVisible: true,
          value: values,
          control
        };
      });
  }

  private getValidRangeLabel(begin: string, end: string): string {
    let label: string;

    if (DATE_MIN === begin && DATE_MAX === end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_begin_to_end';
    } else if (DATE_MIN === begin && DATE_MAX !== end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_begin_to_date';
    } else if (DATE_MIN !== begin && DATE_MAX === end) {
      label = 'EMPLOYEE_MANAGE.Valid_from_date_to_end';
    } else {
      label = 'EMPLOYEE_MANAGE.Valid_from_to';
    }

    return this.translateService.instant(label, {
      validFrom: moment(begin).format(this.localization.dateFormat),
      validTill: moment(end).format(this.localization.dateFormat)
    });
  }

  private checkPaymentsModality(groupZoneDescriptor: GroupZoneDescriptor, all: Array<FormCardFieldComponent>) {
    const paymentsModalityField = all.find(f => f.descriptor.zoneID.includes('.paymentModality'));
    const descriptorsMap = groupZoneDescriptor.metadatas.descriptors;

    paymentsModalityField.control.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
        const zones = getZonesFromMap(descriptorsMap, value);

        if (groupZoneDescriptor.zones !== zones) {
          groupZoneDescriptor.zones = zones;
          const emptyData = this.formGeneratorService.createEmptyData(groupZoneDescriptor)[0];
          emptyData.values[paymentsModalityField.descriptor.zoneID].value = value;
          this.loadFields(emptyData);
        }
      });
  }

  private loadFields(zoneValues: ZoneValues) {
    this.fields.forEach(ref => ref.destroy());
    this.fields = [];

    const promisesOfLoadedObservables = this.getCardFields(zoneValues)
      .map((field: any) => {
        const componentRef = this.fieldComponent.createComponent(
          this.componentFactoryResolver.resolveComponentFactory(FormCardFieldComponent));

        this.fields.push(componentRef);

        Object.assign(componentRef.instance, {
          groupZoneDescriptor: this.activeGroupZone,
          descriptor: field.descriptor,
          value: field.value,
          editMode: true,
          cardColumns: null,
          employeeId: this.cliEmpId,
          employeeContracts: this.employeeContracts,
          profileMode: this.profileMode
        });

        return componentRef.instance.loaded.toPromise();
      });

    Promise.all(promisesOfLoadedObservables).then(fields => {
      if (this.activeGroupZone.internalType === BANKING_DETAILS) {
        this.checkPaymentsModality(this.activeGroupZone, fields);
      }

      const valuesGroup: UntypedFormGroup = this.formBuilder.group({});
      fields.forEach(f => {
        setTimeout(() => {
          f.changeDetectorRef.markForCheck();
          f.control.updateValueAndValidity();
        }, 1000);

        valuesGroup.addControl(f.descriptor.zoneID, this.formBuilder.group({
          type: [f.descriptor.type],
          value: f.control
        }));
      });

      this.effectiveDateControl.reset(moment().startOf('day').format('YYYY-MM-DD'));

      const cardFormGroup: UntypedFormGroup = this.formBuilder.group({
        begindate: this.effectiveDateControl,
        enddate: [zoneValues.enddate],
        isDated: [zoneValues.isDated],
        id: [zoneValues.id],
        groupName: [zoneValues.groupName],
        values: valuesGroup
      });

      this.form = this.formBuilder.group({
        [this.activeGroupZone.groupZoneID]: this.formBuilder.array([cardFormGroup])
      });

      const initialFormValues = this.form.value;
      this.handleFormChanges(initialFormValues, fields);
    });
  }

  public selectContract(contract: Contract): void {
    this.selectedContract = contract;
    this.essService.selectContract(contract.id);
  }

  public customPlaceholderGenerator(contract: Contract) {
    const text: string = this.translate.instant('EMPLOYEE_FORM.Contract_name', {
      name: contract.cliRegNickName
    });

    const contractDetails = `${text} <span class="nowrap">${this.getContractDate(contract)}</span>`;
    return contract.flag
      ? `<img class="flag-icon" src="${contract.flag.href}" alt="flag"/> ${contractDetails}`
      : contractDetails;
  }

  public getContractDate(contract: Contract): string {
    return `(${
      this.dateLocalizationPipe?.transform(contract.begindate)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : this.dateLocalizationPipe?.transform(contract.enddate)
    })`;
  }

  private handleFormChanges(initialFormValues, fields): void {
    this.form.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.componentDestroyed$)
    ).subscribe(currentChanges => {
        fields.forEach(field => field.changeDetectorRef.detectChanges());
        this.isFormDirty = isEqual(initialFormValues, currentChanges) === false;
      }
    );
  }
}
